import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppBar, Tag, BtnLink } from "../components/index.js";
import { Card } from "../components/Card.js";
import { Icon } from "../components/Icon.js";
import { useSurface } from "../lib/useSurface.js";
import { manuals, isOffline } from "../lib/api.js";
import type { JobPhase, JobProgress } from "@tebiki/shared";

const PHASES: { phase: JobPhase; label: string }[] = [
  { phase: "INGEST", label: "動画をアップロード" },
  { phase: "UPLOAD_GEMINI", label: "音声を文字起こし" },
  { phase: "ANALYZE", label: "手順の区切りを検出" },
  { phase: "EXTRACT_FRAMES", label: "候補フレームを抽出" },
  { phase: "ANNOTATE", label: "手順文と注意点を生成" },
  { phase: "ASSEMBLE", label: "表・タイトルを整える" },
];

const ORDER: JobPhase[] = ["INGEST", "UPLOAD_GEMINI", "ANALYZE", "EXTRACT_FRAMES", "ANNOTATE", "ASSEMBLE", "DONE"];

type Conn = "ok" | "error" | "offline";

/**
 * Screen D — AI processing. Polls /api/manuals/:id/status. No demo/fake progression: a failure
 * surfaces an honest offline / error state with retry, and recovers automatically when the API
 * (or the connection) comes back.
 */
export function Processing() {
  useSurface("field");
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [conn, setConn] = useState<Conn>("ok");
  const [nonce, setNonce] = useState(0);
  const advancedRef = useRef(false);
  // A capture that couldn't be uploaded (offline) is parked under a local_ id — not on the server yet.
  const pendingUpload = id.startsWith("local_");

  useEffect(() => {
    if (!id) return;
    if (pendingUpload) {
      setConn("offline");
      return;
    }
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const p = await manuals.status(id);
        if (!alive) return;
        setProgress(p);
        setConn("ok");
        if (p.phase !== "DONE" && p.phase !== "FAILED") timer = setTimeout(poll, 2000);
      } catch {
        if (!alive) return;
        setConn(isOffline() ? "offline" : "error");
        // Keep retrying so processing resumes automatically once connectivity / the API returns.
        timer = setTimeout(poll, 4000);
      }
    };
    void poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [id, nonce, pendingUpload]);

  const phase = progress?.phase;
  const currentIdx = phase ? ORDER.indexOf(phase) : -1;
  const total = progress?.totalSteps ?? 0;
  const completed = progress?.completedSteps ?? 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const done = phase === "DONE";
  const failedJob = phase === "FAILED";

  // AI done → auto-advance to the next step of the flow (撮る→確認→提出): video manuals go
  // through the keyframe picker, image manuals straight to the preview. A short beat lets the
  // "完成しました" state register before the transition.
  useEffect(() => {
    if (!done || advancedRef.current) return;
    advancedRef.current = true;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    void (async () => {
      let target = `/picker/${id}`;
      try {
        const m = await manuals.get(id);
        if (m.sourceType === "image") target = `/preview/${id}`;
      } catch {
        /* can't resolve sourceType — the picker copes with both */
      }
      if (!alive) return;
      timer = setTimeout(() => {
        if (alive) navigate(target, { replace: true });
      }, 900);
    })();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, id]);

  // Honest connection state (no demo data): offline / error with retry.
  if ((conn === "offline" || conn === "error") && !progress) {
    const offline = conn === "offline";
    return (
      <div className="shell">
        <AppBar compactBrand>
          <Tag dot color={offline ? "var(--warn)" : "var(--danger)"}>{offline ? "OFFLINE" : "ERROR"}</Tag>
        </AppBar>
        <main className="proc">
          <Card crop className="proc-card reveal" style={{ textAlign: "center" }}>
            <h1 className="display t-lg" style={{ marginBottom: 10 }}>
              {offline ? (pendingUpload ? "アップロード待ち" : "オフラインです") : "接続できませんでした"}
            </h1>
            <p className="jp" style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.85 }}>
              {offline
                ? "ネットワークに接続すると、撮影した動画を自動で送信し、処理を開始します。"
                : "サーバーに接続できませんでした。通信環境を確認して、もう一度お試しください。"}
            </p>
            <div className="row" style={{ justifyContent: "center", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
              <button className="btn btn--lg" onClick={() => setNonce((n) => n + 1)}>再試行</button>
              <BtnLink to="/dashboard" variant="ghost" lg>ダッシュボードに戻る</BtnLink>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="shell">
      <AppBar compactBrand>
        <Tag dot color={failedJob ? "var(--danger)" : "var(--hi)"}>
          {done ? "DONE" : failedJob ? "FAILED" : "PROCESSING"}
        </Tag>
      </AppBar>

      <main className="proc">
        <Card crop className="proc-card reveal">
          <div className="orbit">
            <div className="ring" />
            <div className="ring spin" style={done ? { animation: "none" } : undefined} />
            <div className="core hl">{done ? <Icon name="check" size={28} /> : failedJob ? <Icon name="warning" size={28} /> : "AI"}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div className="kicker">
              {done ? "完成しました" : failedJob ? "処理に失敗しました" : progress ? "録画完了 — 手離れOK" : "状態を確認中…"}
            </div>
            <h1 className="display t-lg" style={{ margin: "8px 0 4px" }}>
              {done ? "マニュアルができました" : failedJob ? "もう一度お試しください" : "マニュアルを生成中…"}
            </h1>
            <p className="jp" style={{ color: "var(--muted)", fontSize: 14 }}>
              {done
                ? "まもなく確認画面へ進みます…"
                : failedJob
                  ? "動画を撮り直すか、時間をおいて再試行してください。"
                  : "完成したら通知します。このまま待っても、ダッシュボードに戻ってもOK。"}
            </p>
          </div>

          <div className="bar" style={{ margin: "22px 0 6px" }}>
            <i style={{ width: `${done ? 100 : pct}%`, animation: "none" }} />
          </div>
          <div className="spread">
            <span className="pct">{completed} / {total} 手順</span>
            <span className="pct">{done ? "完了" : failedJob ? "—" : "処理中…"}</span>
          </div>

          <div style={{ marginTop: 18 }}>
            {PHASES.map((p, i) => {
              const phaseIdx = ORDER.indexOf(p.phase);
              const state =
                done || (currentIdx >= 0 && phaseIdx < currentIdx)
                  ? "done"
                  : phaseIdx === currentIdx
                    ? "doing"
                    : "todo";
              return (
                <div
                  key={p.phase}
                  className={`stage ${state}`}
                  style={i === PHASES.length - 1 ? { borderBottom: 0 } : undefined}
                >
                  <span className="mk">{state === "done" ? <Icon name="check" size={14} /> : state === "doing" ? <Icon name="spinner" size={14} className="ic-spin" /> : <Icon name="dot-empty" size={10} />}</span>
                  <span className="jp grow">{p.label}</span>
                </div>
              );
            })}
          </div>

          {conn === "error" && progress ? (
            <p className="mono" style={{ fontSize: 11, color: "var(--warn)", marginTop: 14, textAlign: "center" }}>
              接続が不安定です。再接続を試みています…
            </p>
          ) : null}
        </Card>
      </main>

      <div className="actionbar">
        {failedJob ? (
          /* 失敗時の行き止まり回避: 本文の指示（撮り直し）に対応するボタンを実際に出す */
          <>
            <BtnLink to="/dashboard" variant="ghost" lg className="grow">
              ダッシュボードへ
            </BtnLink>
            <BtnLink to="/capture/video" variant="hi" lg icon={<Icon name="capture" size={16} />}>
              もう一度撮影する
            </BtnLink>
          </>
        ) : (
          <>
            <BtnLink to="/dashboard" variant="ghost" lg className="grow">
              ダッシュボードへ（完了で通知）
            </BtnLink>
            <BtnLink to={`/picker/${id}`} variant="hi" lg>
              クリップ確認へ
            </BtnLink>
          </>
        )}
      </div>
    </div>
  );
}
